import type { Party } from '@my/directory-api';
import type { CargoDefinition, CargoDefinitionVariant, CargoItemKindValue } from '@my/tms-api';
import type { JobRow, MatchKind, OrderDraft } from './types';

/** Normalise a company name for fuzzy matching: drop parentheticals, lower-case,
 *  collapse non-alphanumerics to single spaces. */
function normName(s: string): string {
  return s
    .toLowerCase()
    .replace(/\(.*?\)/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Map a cargo definition's type to the cargo-item kind a line should use. */
function cargoKindForType(cargoType: string): CargoItemKindValue {
  switch (cargoType) {
    case 'Container':
      return 'ContainerUnit';
    case 'Tank':
      return 'TankUnit';
    case 'Bulk':
      return 'BulkBatch';
    case 'Livestock':
      return 'LivestockBatch';
    default:
      return 'PalletLine';
  }
}

export interface ResolveContext {
  partiesByCode: Map<string, Party>;
  partiesByName: Map<string, Party>;
  cargoByName: Map<string, CargoDefinition>;
  cargoByType: Map<string, CargoDefinition>;
}

export function buildResolveContext(
  parties: Party[],
  cargoDefs: CargoDefinition[],
): ResolveContext {
  const partiesByCode = new Map<string, Party>();
  const partiesByName = new Map<string, Party>();
  for (const p of parties) {
    if (p.code) partiesByCode.set(p.code.toUpperCase(), p);
    const nn = normName(p.name);
    if (nn) partiesByName.set(nn, p);
  }

  const cargoByName = new Map<string, CargoDefinition>();
  const cargoByType = new Map<string, CargoDefinition>();
  for (const c of cargoDefs) {
    cargoByName.set(c.displayName.trim().toLowerCase(), c);
    if (!cargoByType.has(c.cargoType)) cargoByType.set(c.cargoType, c);
  }

  return { partiesByCode, partiesByName, cargoByName, cargoByType };
}

function resolveCustomer(
  job: JobRow,
  ctx: ResolveContext,
): { id: string | null; label: string; match: MatchKind } {
  const code = job.accountCode.trim().toUpperCase();
  if (code && ctx.partiesByCode.has(code)) {
    const p = ctx.partiesByCode.get(code)!;
    return { id: p.id, label: p.name, match: 'code' };
  }

  const nn = normName(job.clientName);
  if (nn) {
    const exact = ctx.partiesByName.get(nn);
    if (exact) return { id: exact.id, label: exact.name, match: 'name' };
    // Fuzzy: either side a prefix of, or containing, the other.
    for (const [key, p] of ctx.partiesByName) {
      if (key.startsWith(nn) || nn.startsWith(key) || key.includes(nn)) {
        return { id: p.id, label: p.name, match: 'name' };
      }
    }
  }

  return { id: null, label: job.clientName || job.accountCode, match: 'none' };
}

/** Default cargo item for a matched definition — first variant by sortOrder. */
function defaultVariant(def: CargoDefinition): CargoDefinitionVariant | null {
  if (!def.variants || def.variants.length === 0) return null;
  return [...def.variants].sort((a, b) => a.sortOrder - b.sortOrder)[0] ?? null;
}

function resolveCargo(
  job: JobRow,
  ctx: ResolveContext,
): {
  id: string | null;
  label: string;
  kind: CargoItemKindValue;
  match: boolean;
  variantName: string | null;
  perUnitLm: number | null;
} {
  const it = job.itemType.trim();
  const byName = it ? ctx.cargoByName.get(it.toLowerCase()) : undefined;
  const def = byName ?? (it ? ctx.cargoByType.get(it) : undefined);
  if (def) {
    const v = defaultVariant(def);
    return {
      id: def.id,
      label: def.displayName,
      kind: cargoKindForType(def.cargoType),
      match: true,
      variantName: v?.name ?? null,
      perUnitLm: v?.loadingMetres ?? null,
    };
  }
  return { id: null, label: it, kind: 'PalletLine', match: false, variantName: null, perUnitLm: null };
}

export function toDraft(job: JobRow, index: number, ctx: ResolveContext): OrderDraft {
  const c = resolveCustomer(job, ctx);
  const g = resolveCargo(job, ctx);
  return {
    key: `j${index}`,
    job,
    customerPartyId: c.id,
    customerLabel: c.label,
    customerMatch: c.match,
    cargoDefinitionId: g.id,
    cargoLabel: g.label,
    cargoKind: g.kind,
    cargoMatch: g.match,
    cargoVariantName: g.variantName,
    cargoLoadingMetresPerUnit: g.perUnitLm,
    excluded: false,
    status_: 'pending',
  };
}

export function buildDrafts(jobs: JobRow[], ctx: ResolveContext): OrderDraft[] {
  return jobs.map((j, i) => toDraft(j, i, ctx));
}
